import { useEffect, useMemo } from 'react';

import { COMPANY_KEY_PARAM, WAREHOUSE_KEY_PARAM } from '@/common/constants';
import { useAuth } from '@/common/contexts/AuthContext';
import { useDefaultCartContext } from '@/common/contexts/DefaultCartContext';
import useQueryParams from '@/common/hooks/useQueryParams';
import useWarehouses from '@/common/hooks/warehouses/useWarehouses';
import SessionStorageScanbat from '@/common/services/SessionStorage.scanbat';
import useCart from '@/ebat/hooks/carts/useCart';
import useCarts from '@/ebat/hooks/carts/useCarts';

const useScanbatContainer = () => {
  const { logout, user } = useAuth();

  const scanbatService = SessionStorageScanbat.getService();
  const { defaultCart } = useDefaultCartContext();
  const getQueryParams = useQueryParams();

  const {
    data: carts,
    isLoading: isLoadingCarts,
    isFetching: isFetchingCarts,
    isError: isErrorCarts,
    isSuccess: isSuccessCarts,
    refetch: refetchCarts,
  } = useCarts({
    sources: ['scanbat'],
    isLoggedIn: user,
  });

  const {
    data: cart,
    isLoading: isLoadingCart,
    isFetching: isFetchingCart,
    isError: isErrorCart,
    isSuccess: isSuccessCart,
    refetch: refetchCart,
  } = useCart({ id: carts?.data[0]?.id, setDefault: false, source: 'scanbat' });

  const {
    data: warehouse,
    isLoading: isLoadingWarehouse,
    isFetching: isFetchingWarehouse,
    isError: isErrorWarehouse,
    isSuccess: isSuccessWarehouse,
  } = useWarehouses({
    company: scanbatService.getScanbatCompany(),
    code: scanbatService.getScanbatWarehouse(),
  });

  const isDisabled = useMemo(() => {
    const loading =
      isLoadingCarts ||
      isFetchingCarts ||
      isLoadingCart ||
      isFetchingCart ||
      isLoadingWarehouse ||
      isFetchingWarehouse;

    return {
      disabled: loading,
      loading,
    };
  }, [
    isFetchingCart,
    isFetchingCarts,
    isFetchingWarehouse,
    isLoadingCart,
    isLoadingCarts,
    isLoadingWarehouse,
  ]);

  useEffect(() => {
    if (carts?.data.length === 0 && defaultCart) {
      refetchCarts();
    }
  }, [carts, defaultCart, refetchCarts]);

  useEffect(() => {
    if (
      getQueryParams.get(COMPANY_KEY_PARAM) &&
      getQueryParams.get(WAREHOUSE_KEY_PARAM)
    ) {
      scanbatService.setScanbatCompany(getQueryParams.get(COMPANY_KEY_PARAM));
      scanbatService.setScanbatWarehouse(
        getQueryParams.get(WAREHOUSE_KEY_PARAM),
      );
    }

    if (
      !scanbatService.getScanbatCompany() ||
      !scanbatService.getScanbatWarehouse()
    ) {
      scanbatService.clearScanBat();
      logout();
    }
  }, [getQueryParams, logout, scanbatService]);

  return {
    cart,
    company: scanbatService.getScanbatCompany(),
    isDisabled,
    isError: isErrorCarts || isErrorCart || isErrorWarehouse,
    isSuccess: isSuccessCarts && isSuccessCart && isSuccessWarehouse,
    refetchCart,
    warehouse,
  };
};

export default useScanbatContainer;
